/* ==========================================================================
   GEOTECH · Complete the look — self-contained behaviour
   - "+ Add" adds the tile's product to the bag without leaving the page.
   - "Notify me" (dialog mode) collects an email via the store's contact form.
   No dependency on the homepage grid's scripts.
   ========================================================================== */
(() => {
  'use strict';
  if (window.__geoCompleteLook) return;
  window.__geoCompleteLook = true;

  const cartAddUrl = () => `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  async function refreshCartCount() {
    try {
      const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.dispatchEvent(new CustomEvent('geotech:cart-updated', { detail: { cart } }));
    } catch (_) { /* non-critical */ }
  }

  async function addToBag(form, btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    const status = form.querySelector('[data-look-status]');
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    btn.disabled = true;
    if (status) {
      status.removeAttribute('data-error');
      status.textContent = 'Adding to bag…';
    }

    // Open the GEOTECH bag drawer on add (same flow the homepage grid uses),
    // falling back to a silent count refresh if the drawer is not present.
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = new FormData(form);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append('sections', cart.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(btn);
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
        signal: controller.signal,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'add failed');
      btn.classList.add('is-added');
      if (status) status.textContent = 'Added to bag.';

      if (cart && typeof cart.renderContents === 'function') {
        cart.classList.remove('is-empty');
        cart.renderContents(data); // opens the drawer + updates the bag count
      } else {
        await refreshCartCount();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'geotech-complete-the-look',
          productVariantId: body.get('id'),
          cartData: data,
        });
      }

      window.setTimeout(() => {
        btn.classList.remove('is-added');
        if (status) status.textContent = '';
      }, 1600);
    } catch (error) {
      if (status) {
        status.setAttribute('data-error', 'true');
        status.textContent =
          error?.name === 'AbortError'
            ? 'Adding took too long. Please try again.'
            : (error?.message || 'Unable to add this item. Please try again.');
      }
    } finally {
      window.clearTimeout(timeout);
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
      btn.disabled = false;
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.gclook [data-look-add]');
    if (!form) return;
    event.preventDefault();
    const btn = form.querySelector('[data-look-add-btn]');
    if (btn) addToBag(form, btn);
  });

  /* ---- Notify dialog (only used when notify behaviour = dialog) ---- */
  let dialog;
  function ensureDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'gclook-notify';
    dialog.innerHTML = `
      <button type="button" class="gclook-notify__close" data-close aria-label="Close">
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" fill="none"/></svg>
      </button>
      <div class="gclook-notify__inner">
        <h2 class="gclook-notify__title" data-title>Notify me</h2>
        <p class="gclook-notify__copy">Drop your email and we'll message you when it's back.</p>
        <form class="gclook-notify__form" data-notify-form novalidate>
          <label class="visually-hidden" for="GclookNotifyEmail">Email address</label>
          <input id="GclookNotifyEmail" type="email" class="gclook-notify__email" name="contact[email]" autocomplete="email" inputmode="email" placeholder="you@email.com" required>
          <button type="submit" class="gclook-notify__submit">Notify</button>
          <p class="gclook-notify__msg" data-msg role="status" aria-live="polite" aria-atomic="true"></p>
        </form>
      </div>`;
    document.body.appendChild(dialog);
    dialog.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]') || e.target === dialog) dialog.close();
    });
    dialog.querySelector('[data-notify-form]').addEventListener('submit', onNotifySubmit);
    return dialog;
  }

  async function onNotifySubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const msg = form.querySelector('[data-msg]');
    const button = form.querySelector('[type="submit"]');
    const input = form.querySelector('input[name="contact[email]"]');
    const email = input.value.trim();
    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }
    const body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[body]', `Back-in-stock request: ${dialog.dataset.productTitle || ''} (${dialog.dataset.productUrl || ''})`);
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${root()}contact#contact_form`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body, signal: controller.signal });
      if (!response.ok) throw new Error('notify failed');
      msg.removeAttribute('data-error');
      msg.textContent = "You're on the list — we'll be in touch.";
      form.querySelector('input').value = '';
    } catch (error) {
      msg.setAttribute('data-error', 'true');
      msg.textContent = error?.name === 'AbortError'
        ? 'This is taking longer than expected. Please try again.'
        : 'Something went wrong. Please try again.';
    } finally {
      window.clearTimeout(timeout);
      if (button) {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.disabled = false;
      }
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.gclook [data-look-notify]');
    if (!trigger) return;
    const d = ensureDialog();
    d.dataset.productTitle = trigger.dataset.productTitle || '';
    d.dataset.productUrl = trigger.dataset.productUrl || '';
    const title = d.querySelector('[data-title]');
    if (title) title.textContent = trigger.dataset.productTitle ? `Notify me · ${trigger.dataset.productTitle}` : 'Notify me';
    d.querySelector('[data-msg]').textContent = '';
    d.querySelector('[data-msg]').removeAttribute('data-error');
    if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    requestAnimationFrame(() => d.querySelector('input[type="email"]')?.focus({ preventScroll: true }));
  });
})();
